import { useMemo, useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useNavigate } from 'react-router-dom';
import { brandingApi, type Branding } from '../lib/branding';
import { organisationsApi, type Organisation } from '../lib/organisations';
import { usersApi, type UserResponse } from '../lib/users';
import { useAuth } from '../hooks/useAuth';
import { Building2, Plus, Trash2 } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Card } from '@/components/ui/card';
import {
  Dialog,
  DialogContent,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table';
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from '@/components/ui/alert-dialog';

const NONE_BRANDING = '__none__';

function resolveLogoSrc(publicLogoPath: string | null): string | null {
  if (!publicLogoPath) return null;
  if (publicLogoPath.startsWith('http://') || publicLogoPath.startsWith('https://')) {
    return publicLogoPath;
  }
  return publicLogoPath;
}

function formatUserName(user: UserResponse): string {
  const name = [user.firstName, user.lastName].filter(Boolean).join(' ').trim();
  return name || '—';
}

export default function OrganisationPage() {
  const { user: currentUser } = useAuth();
  const queryClient = useQueryClient();
  const navigate = useNavigate();
  const canManage = currentUser?.role === 'super_user';

  const [formOpen, setFormOpen] = useState(false);
  const [editingItem, setEditingItem] = useState<Organisation | null>(null);
  const [name, setName] = useState('');
  const [description, setDescription] = useState('');
  const [brandingId, setBrandingId] = useState<string>(NONE_BRANDING);

  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [itemToDelete, setItemToDelete] = useState<Organisation | null>(null);

  const [uiAlert, setUiAlert] = useState<{
    open: boolean;
    title: string;
    message: string;
  }>({ open: false, title: 'Notice', message: '' });

  const { data: organisations, isLoading: orgsLoading } = useQuery({
    queryKey: ['organisations'],
    queryFn: () => organisationsApi.getAll(),
    enabled: canManage,
  });

  const { data: brandings, isLoading: brandingsLoading } = useQuery({
    queryKey: ['branding'],
    queryFn: () => brandingApi.getAll(),
    enabled: canManage,
  });

  const { data: users, isLoading: usersLoading } = useQuery({
    queryKey: ['users'],
    queryFn: () => usersApi.getAll(),
    enabled: canManage,
  });

  const brandingById = useMemo(() => {
    const map = new Map<string, Branding>();
    brandings?.forEach((b) => map.set(b.id, b));
    return map;
  }, [brandings]);

  const usersByOrganisationId = useMemo(() => {
    const map = new Map<string, UserResponse[]>();
    (users ?? []).forEach((user) => {
      if (!user.organisationId) return;
      const list = map.get(user.organisationId) ?? [];
      list.push(user);
      map.set(user.organisationId, list);
    });
    for (const list of map.values()) {
      list.sort((a, b) => a.email.localeCompare(b.email));
    }
    return map;
  }, [users]);

  const editingOrganisationUsers = useMemo(() => {
    if (!editingItem) return [];
    return usersByOrganisationId.get(editingItem.id) ?? [];
  }, [editingItem, usersByOrganisationId]);

  const isEditing = editingItem !== null;

  const resetForm = () => {
    setEditingItem(null);
    setName('');
    setDescription('');
    setBrandingId(NONE_BRANDING);
  };

  const showError = (error: unknown, fallback: string) => {
    const err = error as { response?: { data?: { error?: string } }; message?: string };
    const errorMessage = err.response?.data?.error || err.message || fallback;
    setUiAlert({ open: true, title: 'Error', message: errorMessage });
  };

  const createMutation = useMutation({
    mutationFn: organisationsApi.create,
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['organisations'] });
      setFormOpen(false);
      resetForm();
    },
    onError: (error) => showError(error, 'Failed to create organisation'),
  });

  const updateMutation = useMutation({
    mutationFn: (payload: { id: string; name: string; description: string | null; brandingId: string | null }) =>
      organisationsApi.update(payload.id, {
        name: payload.name,
        description: payload.description,
        brandingId: payload.brandingId,
      }),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['organisations'] });
      setFormOpen(false);
      resetForm();
    },
    onError: (error) => showError(error, 'Failed to update organisation'),
  });

  const deleteMutation = useMutation({
    mutationFn: (id: string) => organisationsApi.delete(id),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['organisations'] });
      queryClient.invalidateQueries({ queryKey: ['sites'] });
      setDeleteDialogOpen(false);
      setItemToDelete(null);
    },
    onError: (error) => showError(error, 'Failed to delete organisation'),
  });

  const handleOpenCreate = () => {
    resetForm();
    setFormOpen(true);
  };

  const handleOpenEdit = (row: Organisation) => {
    setEditingItem(row);
    setName(row.name);
    setDescription(row.description ?? '');
    setBrandingId(row.brandingId ?? NONE_BRANDING);
    setFormOpen(true);
  };

  const handleRowClick = (row: Organisation, e: React.MouseEvent) => {
    const target = e.target as HTMLElement;
    if (target.closest('button')) return;
    handleOpenEdit(row);
  };

  const handleUserClick = (userId: string) => {
    setFormOpen(false);
    resetForm();
    navigate(`/users/${userId}`);
  };

  const handleSubmitForm = () => {
    const trimmedName = name.trim();
    if (!trimmedName) {
      setUiAlert({ open: true, title: 'Validation', message: 'Please enter an organisation name.' });
      return;
    }
    const payload = {
      name: trimmedName,
      description: description.trim() || null,
      brandingId: brandingId === NONE_BRANDING ? null : brandingId,
    };
    if (isEditing && editingItem) {
      updateMutation.mutate({ id: editingItem.id, ...payload });
    } else {
      createMutation.mutate(payload);
    }
  };

  const handleDeleteClick = (row: Organisation, e: React.MouseEvent) => {
    e.stopPropagation();
    setItemToDelete(row);
    setDeleteDialogOpen(true);
  };

  const confirmDelete = () => {
    if (itemToDelete) {
      deleteMutation.mutate(itemToDelete.id);
    }
  };

  const formPending = createMutation.isPending || updateMutation.isPending;

  if (!canManage) {
    return (
      <div className="px-2 py-3 sm:px-0">
        <p className="text-muted-foreground">You do not have permission to manage organisations.</p>
      </div>
    );
  }

  if (orgsLoading || brandingsLoading || usersLoading) {
    return <div className="p-6">Loading...</div>;
  }

  return (
    <>
      <div className="px-2 py-3 sm:px-0">
        <div className="flex flex-row items-center justify-between gap-4 mb-6 flex-wrap">
          <div className="flex-shrink-0">
            <h1 className="text-2xl sm:text-3xl font-bold font-heading">Organisation</h1>
            <p className="text-sm text-muted-foreground mt-1">
              Organisations link a name and description to branding assets. Click a row to edit.
            </p>
          </div>
          <Button onClick={handleOpenCreate}>
            <Plus className="h-4 w-4 sm:mr-2" />
            <span className="hidden sm:inline">Add Organisation</span>
          </Button>
        </div>

        {!organisations || organisations.length === 0 ? (
          <Card className="text-center py-12">
            <Building2 className="h-10 w-10 mx-auto text-muted-foreground mb-3" />
            <p className="text-muted-foreground">No organisations yet. Add one to get started.</p>
          </Card>
        ) : (
          <Card>
            <div className="overflow-x-auto">
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead className="w-[88px]">Logo</TableHead>
                    <TableHead>Name</TableHead>
                    <TableHead className="hidden md:table-cell max-w-[min(24rem,40vw)]">Description</TableHead>
                    <TableHead className="hidden lg:table-cell text-muted-foreground">Branding</TableHead>
                    <TableHead className="hidden sm:table-cell text-muted-foreground">Users</TableHead>
                    <TableHead className="hidden md:table-cell text-muted-foreground">Updated</TableHead>
                    <TableHead className="text-right w-[100px]">Actions</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {organisations.map((row: Organisation) => {
                    const linked = row.brandingId ? brandingById.get(row.brandingId) : undefined;
                    const logoSrc = linked?.publicLogoPath ? resolveLogoSrc(linked.publicLogoPath) : null;
                    const orgUsers = usersByOrganisationId.get(row.id) ?? [];
                    return (
                      <TableRow
                        key={row.id}
                        onClick={(e) => handleRowClick(row, e)}
                        className="cursor-pointer hover:bg-muted/50"
                      >
                        <TableCell>
                          {logoSrc ? (
                            <img
                              src={logoSrc}
                              alt=""
                              className="h-12 w-12 rounded-md object-contain border border-border bg-muted"
                            />
                          ) : (
                            <div className="h-12 w-12 rounded-md border border-dashed border-muted-foreground/40 flex items-center justify-center text-xs text-muted-foreground">
                              —
                            </div>
                          )}
                        </TableCell>
                        <TableCell className="font-medium">{row.name}</TableCell>
                        <TableCell className="hidden md:table-cell text-sm text-muted-foreground align-top max-w-[min(24rem,40vw)]">
                          {row.description ? (
                            <span className="line-clamp-3 whitespace-pre-wrap">{row.description}</span>
                          ) : (
                            '—'
                          )}
                        </TableCell>
                        <TableCell className="hidden lg:table-cell text-sm text-muted-foreground">
                          {linked?.name ?? '—'}
                        </TableCell>
                        <TableCell className="hidden sm:table-cell text-sm text-muted-foreground">
                          {orgUsers.length}
                        </TableCell>
                        <TableCell className="hidden md:table-cell text-muted-foreground text-sm">
                          {new Date(row.updatedAt).toLocaleString()}
                        </TableCell>
                        <TableCell className="text-right">
                          <Button
                            variant="ghost"
                            size="sm"
                            onClick={(e) => handleDeleteClick(row, e)}
                            disabled={deleteMutation.isPending}
                            className="text-destructive hover:text-destructive"
                            title="Delete organisation"
                          >
                            <Trash2 className="h-4 w-4" />
                            <span className="hidden sm:inline ml-1">Delete</span>
                          </Button>
                        </TableCell>
                      </TableRow>
                    );
                  })}
                </TableBody>
              </Table>
            </div>
          </Card>
        )}
      </div>

      <Dialog
        open={formOpen}
        onOpenChange={(open) => {
          setFormOpen(open);
          if (!open) resetForm();
        }}
      >
        <DialogContent className="sm:max-w-lg max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle>{isEditing ? 'Edit organisation' : 'Add organisation'}</DialogTitle>
          </DialogHeader>
          <div className="grid gap-4 py-2">
            <div className="grid gap-2">
              <Label htmlFor="org-name">Name</Label>
              <Input
                id="org-name"
                value={name}
                onChange={(e) => setName(e.target.value)}
                placeholder="e.g. North Region operations"
                autoComplete="organization"
              />
            </div>
            <div className="grid gap-2">
              <Label htmlFor="org-branding">Branding logo</Label>
              <Select value={brandingId} onValueChange={setBrandingId}>
                <SelectTrigger id="org-branding">
                  <SelectValue placeholder="Choose a branding entry…" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value={NONE_BRANDING}>None</SelectItem>
                  {(brandings ?? []).map((b) => (
                    <SelectItem key={b.id} value={b.id}>
                      {b.name}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
              <p className="text-xs text-muted-foreground">
                Logos are managed on the Branding page. Select a branding record to use its logo here.
              </p>
            </div>
            <div className="grid gap-2">
              <Label htmlFor="org-description">Description</Label>
              <Textarea
                id="org-description"
                value={description}
                onChange={(e) => setDescription(e.target.value)}
                placeholder="Short summary of this organisation"
                rows={4}
                className="resize-y min-h-[5rem]"
              />
            </div>

            {isEditing && (
              <div className="grid gap-2">
                <Label>Users</Label>
                {editingOrganisationUsers.length === 0 ? (
                  <p className="text-sm text-muted-foreground py-2">
                    No users linked to this organisation.
                  </p>
                ) : (
                  <div className="rounded-md border max-h-56 overflow-y-auto">
                    <Table>
                      <TableHeader>
                        <TableRow>
                          <TableHead>Email</TableHead>
                          <TableHead className="hidden sm:table-cell">Name</TableHead>
                          <TableHead>Role</TableHead>
                        </TableRow>
                      </TableHeader>
                      <TableBody>
                        {editingOrganisationUsers.map((user) => (
                          <TableRow
                            key={user.id}
                            className="cursor-pointer hover:bg-muted/50"
                            onClick={() => handleUserClick(user.id)}
                          >
                            <TableCell className="max-w-0">
                              <div className="truncate" title={user.email}>
                                {user.email}
                              </div>
                            </TableCell>
                            <TableCell className="hidden sm:table-cell whitespace-nowrap">
                              {formatUserName(user)}
                            </TableCell>
                            <TableCell className="whitespace-nowrap">
                              <Badge variant="outline">
                                {user.role.replace('_', ' ')}
                              </Badge>
                            </TableCell>
                          </TableRow>
                        ))}
                      </TableBody>
                    </Table>
                  </div>
                )}
                <p className="text-xs text-muted-foreground">
                  Click a user to open their profile. Users are assigned to organisations on the user page.
                </p>
              </div>
            )}
          </div>
          <DialogFooter className="gap-2 sm:gap-0">
            <Button type="button" variant="outline" onClick={() => setFormOpen(false)}>
              Cancel
            </Button>
            <Button type="button" onClick={handleSubmitForm} disabled={formPending}>
              {formPending ? (isEditing ? 'Saving…' : 'Creating…') : isEditing ? 'Save' : 'Create'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <AlertDialog open={deleteDialogOpen} onOpenChange={setDeleteDialogOpen}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Delete organisation?</AlertDialogTitle>
            <AlertDialogDescription>
              {itemToDelete
                ? `This will permanently delete "${itemToDelete.name}". This action cannot be undone.`
                : 'This action cannot be undone.'}
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Cancel</AlertDialogCancel>
            <AlertDialogAction
              onClick={confirmDelete}
              className="bg-destructive text-destructive-foreground hover:bg-destructive/90"
            >
              Delete
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      <AlertDialog open={uiAlert.open} onOpenChange={(open) => setUiAlert((prev) => ({ ...prev, open }))}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>{uiAlert.title}</AlertDialogTitle>
            <AlertDialogDescription>{uiAlert.message}</AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogAction>OK</AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </>
  );
}
